// Items by id (app-shell 2026-09-14, lever 5) — the reads that replace "every item" where a screen only
// ever looked up a handful of rows by id:
//   useLinkedItems(devs)  — the Developments portal's catalogItems: the ~900 items its developments are
//                           based on (DevCardNew's Based-On thumbnail, DevCompareModal, the picker's
//                           selected-item card). Five picker-shaped fields and the FIRST image only —
//                           ~180 KB instead of 6.9 MB; the detail surfaces that show an item's whole
//                           gallery fetch that one row with useItemById.
//   useItemById(id)       — one full row `{ ...data, id }` (DevDetailBasedOnV2, CompletionView, the
//                           picker when the selected item is not a linked one).
//   useItemBrands()       — the brands present in items, canonical case, sorted: DevFormModal2's brand
//                           list (one field of every item, ~240 KB, only when the form opens).
// Ids go through pgIn.chunkIds (Conv #167: a PostgREST `.in()` is bounded by the URL) and every chunk
// is ordered by id. All keyed UNDER ['items'] — the catalog realtime (Tier A) invalidates them.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { chunkIds } from '../utils/pgIn';

const CATALOG_TYPE = 'Original Item (Catalog)';
const STALE = 5 * 60 * 1000, GC = 30 * 60 * 1000;
const EMPTY = [];

// the picker / card shape: flat, `images` an array whose first entry is the thumbnail
export const LINKED_ITEM_SELECT = 'id,b:data->brand,k:data->sku,m:data->model,t:data->material,g:data->images->0';
const flattenLinked = (r) => ({ id: r.id, brand: r.b ?? undefined, sku: r.k ?? undefined, model: r.m ?? undefined, material: r.t ?? undefined, images: r.g ? [r.g] : [] });

export async function fetchItemsByIds(ids, select = LINKED_ITEM_SELECT, map = flattenLinked) {
  const out = [];
  for (const chunk of chunkIds(ids)) {
    const { data, error } = await supabase.from('items').select(select).in('id', chunk).order('id');
    if (error) throw error;
    out.push(...(data || []).map(map));
  }
  return out;
}

// a short stable key for a set of ids — the ids themselves would be a 30 KB query key
const idsKey = (ids) => { let h = 2166136261; for (const id of ids) for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return `${ids.length}:${h.toString(16)}`; };

/** The distinct items the given developments are based on (based_on_data.item_id), sorted. */
export function linkedItemIds(devs) {
  const ids = new Set();
  for (const d of devs || []) {
    if ((d.based_on || d.data?.based_on) !== CATALOG_TYPE) continue;
    const id = (d.based_on_data || d.data?.based_on_data)?.item_id;
    if (typeof id === 'string' && id) ids.add(id);
  }
  return [...ids].sort();
}

export function useLinkedItems(devs, enabled = true) {
  const ids = useMemo(() => linkedItemIds(devs), [devs]);
  const q = useQuery({ queryKey: ['items', 'linked', idsKey(ids)], queryFn: () => fetchItemsByIds(ids), enabled: enabled && ids.length > 0, staleTime: STALE, gcTime: GC });
  return q.data || EMPTY;
}

export function useItemById(id) {
  const q = useQuery({
    queryKey: ['items', 'by-id', id || ''],
    queryFn: async () => { const { data, error } = await supabase.from('items').select('id,data').eq('id', id).maybeSingle(); if (error) throw error; return data ? { ...(data.data || {}), id: data.id } : null; },
    enabled: !!id, staleTime: STALE, gcTime: GC,
  });
  return q.data || null;
}

export async function fetchItemBrands() {
  let rows = [], from = 0;
  for (;;) {
    const { data, error } = await supabase.from('items').select('b:data->>brand').order('id').range(from, from + 999);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  // DevFormModal2's rule, verbatim: canonical case = the first spelling seen, keyed case-insensitively
  const canonical = new Map();
  for (const r of rows) { const raw = r.b; if (!raw) continue; const key = raw.trim().toLowerCase(); if (!canonical.has(key)) canonical.set(key, raw.trim()); }
  return [...canonical.values()].sort();
}

export function useItemBrands(enabled = true) {
  const q = useQuery({ queryKey: ['items', 'brands'], queryFn: fetchItemBrands, enabled, staleTime: STALE, gcTime: GC });
  return q.data || EMPTY;
}
